import React, { createContext, useContext, useState, useCallback } from 'react';
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import it from './locales/it.json';
import de from './locales/de.json';
import zh from './locales/zh.json';

// Locales are produced by U-CogNet itself (Qwen-32B translates en.json — see
// experiments/eu_positioning/translate_site.py). Lightweight, dependency-free
// i18n: a context + a t(key) lookup, language persisted in localStorage.
const LOCALES = { en, es, fr, it, de, zh };

// Octubre 2026: español e inglés. fr/it/de/zh se reactivan cuando se traduzcan los textos nuevos del producto.
export const LANGS = [['es', 'Español'], ['en', 'English']];

function lookup(obj, key) {
  return key.split('.').reduce((o, p) => (o == null ? o : o[p]), obj);
}

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const guardado = localStorage.getItem('ucognet_lang');
      return LANGS.some(([c]) => c === guardado) ? guardado : 'es';
    } catch (e) { return 'es'; }
  });
  const setLang = useCallback((l) => {
    try { localStorage.setItem('ucognet_lang', l); } catch (e) { /* ignore */ }
    setLangState(l);
  }, []);
  const t = useCallback((key) => {
    const v = lookup(LOCALES[lang] || LOCALES.en, key);
    if (typeof v === 'string') return v;
    const fallback = lookup(LOCALES.en, key);       // never show a raw key
    return typeof fallback === 'string' ? fallback : key;
  }, [lang]);
  return <I18nCtx.Provider value={{ lang, setLang, t }}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nCtx);
  if (ctx) return ctx;
  // Safety net outside the provider: still resolve English, never raw keys.
  return {
    lang: 'en', setLang: () => {},
    t: (key) => { const e = lookup(LOCALES.en, key); return typeof e === 'string' ? e : key; },
  };
}
